/**
 * 路由与权限守卫
 *
 * 三层拦截：
 *   1. RequireAuth      —— 未登录一律回登录页（带 redirect，登录后跳回原页面）
 *   2. RequireScope     —— 平台运营端 / 租户端互不串门
 *   3. RequirePermission—— 无权限的路由不渲染（并给出可读的提示，而不是白屏）
 *
 * 再次强调：前端守卫只是体验优化，真正的鉴权在后端接口层。
 */

import { Suspense } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { Button, Result, Spin } from 'antd';
import { PermissionCode } from '@ilc/shared';
import { useAuthStore } from '@/stores/auth';
import { MainLayout } from '@/layouts/MainLayout';

import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Dashboard from '@/pages/Dashboard';
import Profile from '@/pages/Profile';
import NotFound from '@/pages/NotFound';

import HandlerWorkbench from '@/pages/business/HandlerWorkbench';
import InvoiceUpload from '@/pages/business/InvoiceUpload';
import InvoiceDetail from '@/pages/business/InvoiceDetail';
import CheckReportPage from '@/pages/business/CheckReport';
import ReviewerWorkbench from '@/pages/business/ReviewerWorkbench';
import BookkeeperWorkbench from '@/pages/business/BookkeeperWorkbench';
import LegalWorkbench from '@/pages/business/LegalWorkbench';
import Archive from '@/pages/business/Archive';

/* —— 合同全生命周期（延伸需求第一部分）—— */
import ContractLedger from '@/pages/business/ContractLedger';
import ContractDetailPage from '@/pages/business/ContractDetail';
import ContractAlertsPage from '@/pages/business/ContractAlerts';

/* —— 费用报销（延伸需求第二部分）—— */
import ExpenseApplyPage from '@/pages/business/ExpenseApply';
import ExpenseReimbursePage from '@/pages/business/ExpenseReimburse';
import ExpenseReimburseDetailPage from '@/pages/business/ExpenseReimburseDetail';
import ExpenseReviewPage from '@/pages/business/ExpenseReview';
import ExpensePayPage from '@/pages/business/ExpensePay';
import ExpenseBudgetPage from '@/pages/business/ExpenseBudget';
import ExpenseReportsPage from '@/pages/business/ExpenseReports';
import ExpenseAlertsPage from '@/pages/business/ExpenseAlerts';

/* —— 跨业务类型的待办与审批 —— */
import ApprovalCenterPage from '@/pages/business/ApprovalCenter';

import Users from '@/pages/admin/Users';
import Roles from '@/pages/admin/Roles';
import Depts from '@/pages/admin/Depts';
import FlowTemplates from '@/pages/admin/FlowTemplates';
import TenantConfigPage from '@/pages/admin/TenantConfig';
import Providers from '@/pages/admin/Providers';
import ProviderLogs from '@/pages/admin/ProviderLogs';

import PlatformTenants from '@/pages/platform/Tenants';
import Plans from '@/pages/platform/Plans';
import Metrics from '@/pages/platform/Metrics';
import AuditLogs from '@/pages/platform/AuditLogs';

/* -------------------------------------------------------------------------
 * 守卫
 * ---------------------------------------------------------------------- */

function BootScreen(): React.ReactElement {
  return (
    <div className="ilc-boot">
      <Spin size="large" />
      <span style={{ color: 'rgba(0,0,0,0.45)' }}>正在恢复登录状态…</span>
    </div>
  );
}

function RequireAuth(): React.ReactElement {
  const auth = useAuthStore((s) => s.auth);
  const bootstrapped = useAuthStore((s) => s.bootstrapped);
  const location = useLocation();

  if (!auth && !bootstrapped) return <BootScreen />;
  if (!auth) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }
  return <Outlet />;
}

interface GuardProps {
  /**
   * 需要的权限码。传数组表示 **OR**（任一即可）。
   *
   * 【为什么需要数组】有些页面天然是跨业务的——「待办与审批」要同时服务
   * 财务初审（审发票与报销）、法务（审合同）、出纳（付款节点）与领导。
   * 用一个码表达不了"三者任一"，而硬塞一个所有人都得持有的码，
   * 等于**给所有人多授一项权限**。数组让它如实表达 OR。
   */
  permission?: string | string[];
  /** 限定平台运营端 */
  platform?: boolean;
  /** 限定租户端 */
  tenant?: boolean;
  children: React.ReactElement;
}

/** 无权限时的提示页 —— 明确告诉用户「为什么看不到」，而不是跳走或白屏 */
function Forbidden({ permission, platform }: { permission?: string; platform?: boolean }): React.ReactElement {
  const auth = useAuthStore((s) => s.auth);
  const roles = auth?.roles.map((r) => r.name).join('、') || '当前角色';
  return (
    <Result
      status="403"
      title="无访问权限"
      subTitle={
        permission
          ? `「${roles}」不具备该功能权限${permission ? `（${permission}）` : ''}。如确需使用，请联系租户管理员在「角色权限」中分配。`
          : platform
            ? '该页面仅平台运营人员可访问。'
            : '该页面当前账号不可访问。'
      }
      extra={
        <Button type="primary" href="/dashboard">
          返回工作台
        </Button>
      }
    />
  );
}

function RequireScope({ permission, platform, tenant, children }: GuardProps): React.ReactElement {
  const auth = useAuthStore((s) => s.auth);
  const can = useAuthStore((s) => s.can);

  if (!auth) return <Navigate to="/login" replace />;

  const isPlatform = auth.principalType === 'PLATFORM';
  if (platform && !isPlatform) return <Forbidden platform />;
  if (tenant && isPlatform) return <Forbidden />;

  if (permission) {
    const codes = Array.isArray(permission) ? permission : [permission];
    const ok = codes.some((code) => can(code));
    if (!ok) return <Forbidden permission={codes.join(' 或 ')} />;
  }

  return children;
}

/** 路由表：permission / scope 写在一处，菜单与守卫共用同一份权限口径 */
interface RouteDef {
  path: string;
  element: React.ReactElement;
  /** 权限码；数组表示 OR（任一即可）。见 GuardProps.permission 的说明 */
  permission?: string | string[];
  platform?: boolean;
  tenant?: boolean;
}

const TENANT_ROUTES: RouteDef[] = [
  { path: '/dashboard', element: <Dashboard />, tenant: true },
  { path: '/business/invoices', element: <HandlerWorkbench />, permission: PermissionCode.INVOICE_READ, tenant: true },
  { path: '/business/invoices/:id', element: <InvoiceDetail />, permission: PermissionCode.INVOICE_READ, tenant: true },
  {
    path: '/business/invoices/:id/report',
    element: <CheckReportPage />,
    permission: PermissionCode.CHECK_READ,
    tenant: true,
  },
  { path: '/business/upload', element: <InvoiceUpload />, permission: PermissionCode.INVOICE_CREATE, tenant: true },
  { path: '/business/review', element: <ReviewerWorkbench />, permission: PermissionCode.INVOICE_REVIEW, tenant: true },
  {
    path: '/business/bookkeeping',
    element: <BookkeeperWorkbench />,
    permission: PermissionCode.TPLUS_READ,
    tenant: true,
  },
  { path: '/business/legal', element: <LegalWorkbench />, permission: PermissionCode.CONTRACT_READ, tenant: true },
  { path: '/business/archive', element: <Archive />, permission: PermissionCode.ARCHIVE_READ, tenant: true },

  /* —— 合同全生命周期 —— */
  {
    path: '/business/contracts',
    element: <ContractLedger />,
    permission: PermissionCode.CONTRACT_READ,
    tenant: true,
  },
  {
    /* ⚠ 放在 /business/contracts 之后、/business/contract-alerts 之前都可——
     * React Router 6 按**最长匹配**选路由，不像 Express 那样怕被 /:id 吞掉。
     * 但保持"固定段在前"的顺序仍然更易读，也避免将来换成别的路由库时踩坑。 */
    path: '/business/contracts/:id',
    element: <ContractDetailPage />,
    permission: PermissionCode.CONTRACT_READ,
    tenant: true,
  },
  {
    path: '/business/contract-alerts',
    element: <ContractAlertsPage />,
    permission: PermissionCode.CONTRACT_READ,
    tenant: true,
  },

  /* —— 费用报销 —— */
  {
    path: '/business/expenses/apply',
    element: <ExpenseApplyPage />,
    permission: PermissionCode.EXPENSE_APPLY,
    tenant: true,
  },
  {
    path: '/business/expenses/reimburse',
    element: <ExpenseReimbursePage />,
    permission: PermissionCode.EXPENSE_READ,
    tenant: true,
  },
  {
    path: '/business/expenses/reimburse/:id',
    element: <ExpenseReimburseDetailPage />,
    permission: PermissionCode.EXPENSE_READ,
    tenant: true,
  },
  {
    path: '/business/expenses/review',
    element: <ExpenseReviewPage />,
    permission: PermissionCode.EXPENSE_REVIEW,
    tenant: true,
  },
  {
    path: '/business/expenses/pay',
    element: <ExpensePayPage />,
    permission: PermissionCode.EXPENSE_PAY,
    tenant: true,
  },
  {
    path: '/business/expenses/budget',
    element: <ExpenseBudgetPage />,
    permission: PermissionCode.EXPENSE_BUDGET_MANAGE,
    tenant: true,
  },
  {
    path: '/business/expenses/reports',
    element: <ExpenseReportsPage />,
    permission: PermissionCode.EXPENSE_REPORT,
    tenant: true,
  },
  {
    path: '/business/expenses/alerts',
    element: <ExpenseAlertsPage />,
    permission: PermissionCode.EXPENSE_READ,
    tenant: true,
  },

  /* —— 待办与审批（合同 + 费用 + 发票）—— */
  {
    path: '/business/approvals',
    element: <ApprovalCenterPage />,
    /* 入口权限取并集：合同、费用、发票三类，任一读权限都能进。
     * 具体看得到哪些类型的待办由后端按权限逐个判定收口——中间件是 OR 语义，
     * 往参数里加码只会放宽，所以真正的收口在处理器里。 */
    permission: [
      PermissionCode.INVOICE_READ,
      PermissionCode.CONTRACT_READ,
      PermissionCode.EXPENSE_READ,
    ],
    tenant: true,
  },

  { path: '/admin/users', element: <Users />, permission: PermissionCode.USER_READ, tenant: true },
  { path: '/admin/roles', element: <Roles />, permission: PermissionCode.ROLE_READ, tenant: true },
  { path: '/admin/depts', element: <Depts />, permission: PermissionCode.DEPT_READ, tenant: true },
  {
    path: '/admin/flow-templates',
    element: <FlowTemplates />,
    permission: PermissionCode.FLOW_TEMPLATE_READ,
    tenant: true,
  },
  {
    path: '/admin/tenant-config',
    element: <TenantConfigPage />,
    permission: PermissionCode.TENANT_CONFIG_READ,
    tenant: true,
  },
  { path: '/admin/providers', element: <Providers />, permission: PermissionCode.PROVIDER_CONFIG_READ, tenant: true },
  {
    path: '/admin/provider-logs',
    element: <ProviderLogs />,
    permission: PermissionCode.PROVIDER_LOG_READ,
    tenant: true,
  },
];

const PLATFORM_ROUTES: RouteDef[] = [
  { path: '/platform/metrics', element: <Metrics />, platform: true },
  { path: '/platform/tenants', element: <PlatformTenants />, platform: true },
  { path: '/platform/plans', element: <Plans />, platform: true },
  { path: '/platform/audit-logs', element: <AuditLogs />, platform: true },
];

function renderRoutes(defs: RouteDef[]): React.ReactElement[] {
  return defs.map((def) => (
    <Route
      key={def.path}
      path={def.path}
      element={
        <RequireScope permission={def.permission} platform={def.platform} tenant={def.tenant}>
          {def.element}
        </RequireScope>
      }
    />
  ));
}

/** 首页按主体类型分流：平台运营 → 运营概览，租户用户 → 工作台 */
function HomeRedirect(): React.ReactElement {
  const auth = useAuthStore((s) => s.auth);
  const target = auth?.principalType === 'PLATFORM' ? '/platform/metrics' : '/dashboard';
  return <Navigate to={target} replace />;
}

export function AppRoutes(): React.ReactElement {
  return (
    <Suspense fallback={<BootScreen />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />

        <Route element={<RequireAuth />}>
          <Route element={<MainLayout />}>
            <Route index element={<HomeRedirect />} />
            <Route path="/profile" element={<Profile />} />
            {renderRoutes(TENANT_ROUTES)}
            {renderRoutes(PLATFORM_ROUTES)}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
